import React from 'react';
import dataSources from '../../data/dataSources.json';

interface SidebarProps {
  sectionsExpanded?: {
    about: boolean;
    sources: boolean;
  };
  onToggleSection?: (section: 'about' | 'sources') => void;
  isMobile?: boolean;
  activeSection?: 'about' | 'sources';
  onClose?: () => void;
  t: any;
}

const Sidebar: React.FC<SidebarProps> = ({
  sectionsExpanded,
  onToggleSection,
  isMobile = false,
  activeSection,
  onClose,
  t
}) => {
  const getPrimarySourceIcon = (types: string[]) => {
    const iconMap: { [key: string]: string } = {
      hiking: '🥾',
      biking: '🚴',
      attractions: '🏛️',
      events: '🎉',
      official: '📋'
    };
    
    return iconMap[types[0]] || '📍';
  };

  if (isMobile) {
    return (
      <>
        {/* Backdrop */}
        <div 
          className="lg:hidden fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
          onClick={onClose}
        />
        
        {/* Modal Content */}
        <div className="lg:hidden fixed inset-4 z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-md max-h-[80vh] overflow-hidden">
            
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 flex items-center justify-between">
              <span className="font-medium">
                {activeSection === 'about' ? 'About This Agent' : 'Data Sources'}
              </span>
              <button
                onClick={onClose}
                className="text-white hover:bg-white/20 p-1 rounded transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="overflow-y-auto max-h-full p-4">
              
              {activeSection === 'about' && (
                <div className="space-y-3">
                  <div className="text-sm text-gray-700 leading-relaxed">
                    {t('sidebar.aboutDescription')}
                  </div>
                  
                  <div>
                    <div className="text-xs font-semibold text-gray-800 mb-2">
                      {t('sidebar.aboutFeatures.title')}
                    </div>
                    <ul className="space-y-2">
                      <li className="flex items-start space-x-2">
                        <span className="text-emerald-500 mt-0.5 text-xs">•</span>
                        <span className="text-xs text-gray-600 leading-snug">{t('sidebar.aboutFeatures.realTimeData')}</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-emerald-500 mt-0.5 text-xs">•</span>
                        <span className="text-xs text-gray-600 leading-snug">{t('sidebar.aboutFeatures.localExpertise')}</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-emerald-500 mt-0.5 text-xs">•</span>
                        <span className="text-xs text-gray-600 leading-snug">{t('sidebar.aboutFeatures.smartPlanning')}</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="text-xs text-gray-500 italic pt-2 border-t border-gray-100">
                    {t('sidebar.aboutFooter')}
                  </div>
                </div>
              )}

              {activeSection === 'sources' && (
                <div className="space-y-3">
                  {dataSources.sources.map((source) => (
                    <div key={source.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm">{getPrimarySourceIcon(source.types)}</span>
                          <span className="text-sm font-medium text-gray-900 truncate">{source.name}</span>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {source.types.map((type) => (
                            <span 
                              key={type} 
                              className={`text-xs px-1.5 py-0.5 rounded-full ${
                                type === 'hiking' ? 'bg-green-100 text-green-600' :
                                type === 'biking' ? 'bg-blue-100 text-blue-600' :
                                type === 'attractions' ? 'bg-purple-100 text-purple-600' :
                                type === 'events' ? 'bg-orange-100 text-orange-600' :
                                'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {t(`dataTypes.${type}`)}
                            </span>
                          ))}
                        </div>
                      </div>
                      <a 
                        href={source.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-emerald-600 hover:text-emerald-700 text-sm ml-2 flex-shrink-0"
                      >
                        →
                      </a>
                    </div>
                  ))}
                </div>
              )}

            </div>
          </div>
        </div>
      </>
    );
  }

  // Desktop sidebar
  return (
    <div className="hidden lg:block w-80 xl:w-96 flex-shrink-0">
      <div className="h-full overflow-y-auto bg-gray-50 rounded-2xl border border-gray-200 p-4 space-y-4">
        {/* About Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
          <button
            onClick={() => onToggleSection?.('about')}
            className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white p-3 flex items-center justify-between text-left"
          >
            <div className="flex items-center space-x-2">
              <span className="text-sm">ℹ️</span>
              <span className="font-medium text-sm">{t('sidebar.aboutTitle')}</span>
            </div>
            <span className="text-base">{sectionsExpanded?.about ? '−' : '+'}</span>
          </button>
          {sectionsExpanded?.about && (
            <div className="p-3 space-y-2">
              <div className="text-sm text-gray-700 leading-relaxed">
                {t('sidebar.aboutDescription')}
              </div>
              
              <div>
                <div className="text-xs font-semibold text-gray-800 mb-1.5">
                  {t('sidebar.aboutFeatures.title')}
                </div>
                <ul className="space-y-1.5">
                  <li className="flex items-start space-x-2">
                    <span className="text-emerald-500 mt-0.5 text-xs">•</span>
                    <span className="text-xs text-gray-600 leading-snug">{t('sidebar.aboutFeatures.realTimeData')}</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-emerald-500 mt-0.5 text-xs">•</span>
                    <span className="text-xs text-gray-600 leading-snug">{t('sidebar.aboutFeatures.localExpertise')}</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-emerald-500 mt-0.5 text-xs">•</span>
                    <span className="text-xs text-gray-600 leading-snug">{t('sidebar.aboutFeatures.smartPlanning')}</span>
                  </li>
                </ul>
              </div>
              
              <div className="text-xs text-gray-500 italic pt-1.5 border-t border-gray-100">
                {t('sidebar.aboutFooter')}
              </div>
            </div>
          )}
        </div>

        {/* Data Sources Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
          <button
            onClick={() => onToggleSection?.('sources')}
            className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 text-white p-3 flex items-center justify-between text-left"
          >
            <div className="flex items-center space-x-2">
              <span className="text-sm">🔗</span>
              <span className="font-medium text-sm">{t('sidebar.dataSourcesTitle')}</span>
            </div>
            <span className="text-base">{sectionsExpanded?.sources ? '−' : '+'}</span>
          </button>
          {sectionsExpanded?.sources && (
            <div className="p-2 space-y-1">
              {dataSources.sources.map((source) => (
                <div key={source.id} className="flex items-center space-x-2 p-1.5 bg-gray-50 rounded hover:bg-gray-100 transition-colors">
                  <span className="text-sm flex-shrink-0">{getPrimarySourceIcon(source.types)}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-1">
                      <h4 className="font-medium text-xs text-gray-900 truncate">{source.name}</h4>
                      <a 
                        href={source.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-emerald-600 hover:text-emerald-700 hover:underline flex-shrink-0"
                      >
                        →
                      </a>
                    </div>
                    <div className="flex flex-wrap gap-0.5">
                      {source.types.map((type) => (
                        <span 
                          key={type} 
                          className={`text-xs px-1 py-0.5 rounded ${
                            type === 'hiking' ? 'bg-green-100 text-green-600' :
                            type === 'biking' ? 'bg-blue-100 text-blue-600' :
                            type === 'attractions' ? 'bg-purple-100 text-purple-600' :
                            type === 'events' ? 'bg-orange-100 text-orange-600' :
                            'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {t(`dataTypes.${type}`)}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;